import React, { useEffect, useRef, useState } from 'react';
import { useInViewOnce } from '../hooks/useInViewOnce';

const cents = (c: number) => `R$ ${(c / 100).toFixed(2).replace('.', ',')}`;

export interface EvolutionPoint {
  label: string;
  salesCount: number;
  commissionCents: number;
}

interface Props {
  data: EvolutionPoint[];
  color?: string;
}

// Gráfico de barras 100% CSS (sem SVG) — dá controle total sobre espessura,
// largura cheia e a curva de "crescimento plástico" das barras.
//
// A animação é aplicada de forma IMPERATIVA (manipulando `el.style` direto via
// ref), a mesma técnica já comprovada em RyanMonthlyLineChart (getTotalLength +
// duplo requestAnimationFrame). Depender de state do React pra alternar entre
// "altura 0%" e "altura final" (como na primeira tentativa) não garante que o
// navegador chegue a pintar um frame real com 0% antes do commit já ir direto
// pro valor final — a mutação direta do DOM elimina essa incerteza: zera a
// altura sem transição, espera dois frames (garantindo que o 0% foi pintado),
// e só então liga a transição e aplica a altura de verdade.
const RyanWeeklyBarChart: React.FC<Props> = ({ data, color = '#2F7130' }) => {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const barRefs = useRef<(HTMLDivElement | null)[]>([]);
  const max = Math.max(1, ...data.map(d => d.commissionCents));
  const hasData = data.some(d => d.commissionCents > 0);

  useEffect(() => {
    if (!inView) return;
    data.forEach((d, i) => {
      const el = barRefs.current[i];
      if (!el) return;
      const pct = Math.max((d.commissionCents / max) * 100, d.commissionCents > 0 ? 4 : 0);
      el.style.transition = 'none';
      el.style.height = '0%';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.transition = `height 1200ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 45}ms`;
          el.style.height = `${pct}%`;
        });
      });
    });
  }, [inView, data, max]);

  return (
    <div className="rwc-card" ref={ref}>
      {!hasData ? (
        <div className="rwc-empty">Sem dados suficientes ainda.</div>
      ) : (
        <div className="rwc-track">
          {data.map((d, i) => (
            <div
              key={d.label}
              className="rwc-col"
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {activeIndex === i && (
                <div className="rwc-tooltip">
                  <div className="rwc-tooltip-value">{cents(d.commissionCents)}</div>
                  <div className="rwc-tooltip-sub">{d.salesCount} venda{d.salesCount !== 1 ? 's' : ''}</div>
                </div>
              )}
              <div className="rwc-bar-wrap">
                <div
                  ref={el => { barRefs.current[i] = el; }}
                  className="rwc-bar"
                  style={{ height: '0%', background: color }}
                />
              </div>
              <span className="rwc-label">{d.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RyanWeeklyBarChart;
